import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { IWarehouse, Nullable } from '@nova/core';
import checkoutService from '@/services/checkoutService';
import { setFeatureFlagOrgId, setFeatureFlagWarehouseId } from '@/plugins/featureFlagPlugin';

const isLoading = ref<boolean>(true);
const warehouseData = ref<Nullable<Partial<IWarehouse>>>(null);

export function useWarehouseInfo() {
  const route = useRoute();
  const router = useRouter();
  const checkoutApi = checkoutService();

  const fetchAndStoreWarehouseInfo = async () => {
    try {
      const warehouseId = route.query.warehouseId as string;

      if (!warehouseId) {
        isLoading.value = false;
        await router.push({
          name: 'warehouse-error'
        });
        return;
      }

      // Applied before the first request: the warehouse fetch itself is behind enable-juno-bff.
      try {
        await setFeatureFlagWarehouseId(warehouseId);
      } catch (error) {
        console.error('Error applying the feature flag warehouse context:', error);
      }

      const warehouseRequestData = await checkoutApi.getWarehouseInformation(warehouseId);

      warehouseData.value = warehouseRequestData;

      // The org is only known once the warehouse loads; it joins the warehouse in the context.
      try {
        await setFeatureFlagOrgId(warehouseRequestData.orgId);
      } catch (error) {
        console.error('Error applying the feature flag org context:', error);
      }

      isLoading.value = false;
    } catch (error) {
      isLoading.value = false;
      console.error('Error fetching warehouse information:', error);
      await router.push({
        name: 'warehouse-error'
      });
    }
  };

  return { fetchAndStoreWarehouseInfo, isLoading, warehouseData };
}
